import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
	getIntegrations,
	getIntegration,
	saveIntegration,
	importIntegrationExcel,
	getIntegrationPurchase,
	getIntegrationUsageLookups,
	getIntegrationUsage,
	saveIntegrationParams,
	resendIntegration,
	pendingIntegration,
	syncOrders,
	syncOrdersDelivery,
	syncOrdersSale,
	syncDataFile,
} from "@/api/Integrations/integration-api";

export const key = "integrations";

export function useIntegrations(filters) {
	return useQuery([key, "list", filters], () => getIntegrations(filters));
}

export function useIntegration(id_element, enabled) {
	return useQuery([key, "detail", id_element], () => getIntegration(id_element), {
		enabled: enabled,
		keepPreviousData: true,
	});
}

export function useSaveIntegration() {
	const queryClient = useQueryClient();

	return useMutation(saveIntegration, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;
			const detail = response.detail;

			queryClient.setQueryData([key, "detail", filters, detail.id], detail);
			queryClient.setQueryData([key, "list", filters], list);
		},
	});
}

export function useImportIntegrationExcel() {
	return useMutation(importIntegrationExcel, {
		onSuccess: (_data) => {},
	});
}

export function useIntegrationPurchases(filters, enabled) {
	return useQuery([key, "list-purchase", filters], () => getIntegrationPurchase(filters), {
		enabled: enabled,
	});
}

export function usePendingPurchase() {
	const queryClient = useQueryClient();

	return useMutation(pendingIntegration, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list-purchase", filters], list);
		},
	});
}

// Usage
export function useIntegrationUsagesLookups() {
	return useQuery([key, "list-usage-lookups"], () => getIntegrationUsageLookups());
}

export function useIntegrationUsages(filters, enabled) {
	return useQuery([key, "list-usage", filters], () => getIntegrationUsage(filters), {
		enabled: enabled,
	});
}

export function useSaveIntegrationParams() {
	const queryClient = useQueryClient();

	return useMutation(saveIntegrationParams, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list-usage", filters], list);
		},
	});
}

export function useResendIntegration() {
	const queryClient = useQueryClient();

	return useMutation(resendIntegration, {
		onSuccess: (response) => {
			const filters = response.filters;
			const list = response.data;

			queryClient.setQueryData([key, "list-usage", filters], list);
		},
	});
}

const SYNC_STEPS = {
	sapbo: ["orders", "delivery", "sale"],
	nisira: ["orders", "delivery"],
	agri: ["orders", "delivery", "sale"],
	softland: ["orders"],
	softland_connector: ["orders"],
	odoo: ["orders"],
};

const STEP_API = {
	orders: syncOrders,
	delivery: syncOrdersDelivery,
	sale: syncOrdersSale,
};

const STEP_ERROR_MESSAGES = {
	orders: "Ha ocurrido un error al sincronizar las ordenes de compra.",
	delivery: "Ha ocurrido un error al sincronizar las guías de despacho.",
	sale: "Ha ocurrido un error al sincronizar las facturas.",
};

const INITIAL_SYNC_STATE = {
	orders: { done: false, hasError: false, messageLines: [] },
	delivery: { done: false, hasError: false, messageLines: [] },
	sale: { done: false, hasError: false, messageLines: [] },
	finished: false,
};

const SAPBO_SYNC_STEP_KEY = {
	orders: ["orders", "orders_status"],
	delivery: ["delivery"],
	sale: ["sale"],
};

function getSapboSyncSteps(response) {
	return response?.sync?.steps ?? [];
}

function getSapboUserLines(step, response) {
	const steps = getSapboSyncSteps(response);
	const stepKeys = SAPBO_SYNC_STEP_KEY[step] ?? [step];

	if (steps.length > 0) {
		return steps.filter((item) => stepKeys.includes(item.step)).flatMap((item) => item.user_lines ?? []);
	}

	if (response?.user_lines?.length) {
		return response.user_lines;
	}

	if (response?.message) {
		return String(response.message)
			.split("\n")
			.map((line) => line.trim())
			.filter(Boolean);
	}

	return [];
}

function sapboSyncStepFailed(stepItem) {
	if (stepItem?.fetch?.sap_error || stepItem?.enqueue?.sap_error) {
		return true;
	}

	if ((stepItem?.process?.failed ?? 0) > 0) {
		return true;
	}

	if (stepItem?.step === "orders_status" && stepItem?.extra) {
		if ((stepItem.extra.sap_errors ?? 0) > 0 || (stepItem.extra.not_found_in_sap ?? 0) > 0) {
			return true;
		}
	}

	if (stepItem?.success === false && !stepItem?.process && !stepItem?.fetch && !stepItem?.enqueue) {
		return true;
	}

	return false;
}

function sapboStepHasError(step, response) {
	const steps = getSapboSyncSteps(response);
	const stepKeys = SAPBO_SYNC_STEP_KEY[step] ?? [step];
	const relevantSteps = steps.filter((item) => stepKeys.includes(item.step));

	if (relevantSteps.length === 0) {
		return response?.status === false;
	}

	return relevantSteps.some(sapboSyncStepFailed);
}

function resolveSapboStepResult(step, response) {
	const hasError = sapboStepHasError(step, response);
	const messageLines = hasError ? getSapboUserLines(step, response) : [];

	if (hasError && messageLines.length === 0) {
		messageLines.push(STEP_ERROR_MESSAGES[step]);
	}

	return { done: true, hasError, messageLines };
}

function resolveSyncStepResult(integration, step, response) {
	if (integration === "softland_connector" && step === "orders") {
		const success = response?.success === true;
		const fallbackMessage = response?.message || buildSoftlandConnectorFallbackMessage(response);
		return { done: true, hasError: !success, messageLines: success ? [] : [fallbackMessage] };
	}

	if (integration === "sapbo" && SAPBO_SYNC_STEP_KEY[step]) {
		return resolveSapboStepResult(step, response);
	}

	const hasError = response?.status === false;
	return {
		done: true,
		hasError,
		messageLines: hasError ? [response?.message || STEP_ERROR_MESSAGES[step]].filter(Boolean) : [],
	};
}

function buildSoftlandConnectorFallbackMessage(response) {
	const failedSteps = [];

	if (response?.response_products === false) failedSteps.push("productos");
	if (response?.response_warehouses === false) failedSteps.push("bodegas");
	if (response?.response_business_center === false) failedSteps.push("centros de costo");
	if (response?.response_purchases === false) failedSteps.push("ingresos de stock");

	if (failedSteps.length === 0) {
		return "La sincronización no se completó correctamente.";
	}

	return "Revise la configuración de la integración.";
}

export function useSyncIntegration() {
	const [syncState, setSyncState] = useState(INITIAL_SYNC_STATE);

	const { mutate: runSync } = useMutation(
		async ({ integration, file }) => {
			setSyncState(INITIAL_SYNC_STATE);

			if (file) {
				await syncDataFile({ integration, file });
				const steps = SYNC_STEPS[integration] ?? [];
				const doneSteps = Object.fromEntries(steps.map((step) => [step, { done: true, hasError: false, messageLines: [] }]));
				setSyncState((prev) => ({ ...prev, ...doneSteps }));
				await new Promise((resolve) => setTimeout(resolve, 2000));
				return;
			}

			const steps = SYNC_STEPS[integration] ?? [];
			for (const step of steps) {
				try {
					const response = await STEP_API[step](integration);
					const stepResult = resolveSyncStepResult(integration, step, response);
					setSyncState((prev) => ({ ...prev, [step]: stepResult }));
				} catch {
					setSyncState((prev) => ({
						...prev,
						[step]: { done: true, hasError: true, messageLines: [STEP_ERROR_MESSAGES[step]] },
					}));
				}
			}
		},
		{
			onSuccess: () => setSyncState((prev) => ({ ...prev, finished: true })),
			onError: () => {
				toast.error("Ha ocurrido un error al sincronizar.");
				setSyncState((prev) => ({ ...prev, finished: true }));
			},
		},
	);

	return { syncState, runSync };
}
